import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { PropTypes as G4PropTypes } from '@g4/lynx';
import hooks from '@g4/automation-hooks';
import styled, { css } from 'styled-components';
import oldTheme from '../../Colors';

import iconActive from '../../../../images/boneyard/icon-24-radio-blue.svg';
import iconInactive from '../../../../images/boneyard/icon-24-radio-gray.svg';
import iconActiveDisabled from '../../../../images/boneyard/radio_active_disabled_s.svg';
import iconInactiveDisabled from '../../../../images/boneyard/radio_inactive_disabled_s.svg';

const DIRECTION_ROW = 'row';
const DIRECTION_COLUMN = 'column';

const RadioIcon = styled.img`
  margin-right: 10px;
  cursor: pointer;
  display: inline-block;
  height: 20px;
  width: 20px;
  background-size: cover;
  vertical-align: middle;
  pointer-events: none;

  ${props =>
    props.direction === DIRECTION_COLUMN &&
    css`
      margin-top: 5px;
      margin-right: 0;
    `};
`;

const RadioInput = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;

  &:focus + ${RadioIcon} {
    outline: 0;
    box-shadow: 0 0 10px
      ${props => oldTheme.colors.modern.checkbox.activeShadow};
    border-radius: 50%;
  }
`;

export class Radio extends PureComponent {
  static propTypes = {
    automationHook: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
      .isRequired,
    labelClasses: G4PropTypes.labelClasses,
    value: PropTypes.any.isRequired,
    size: PropTypes.oneOf(['small']), // TODO allow more options for sizing maybe? if the design calls for it
    direction: PropTypes.oneOf([DIRECTION_ROW, DIRECTION_COLUMN]),
    name: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    wrapper: PropTypes.element,
    checkedIconAltAttribute: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    checked: PropTypes.bool,
  };

  static defaultProps = {
    labelClasses: {
      checked: '',
      unchecked: '',
    },
    direction: DIRECTION_ROW,
    disabled: false,
  };

  handleChange = event => {
    if (this.props.onChange) {
      this.props.onChange(event, this.props.value);
    }
  };

  handleInputClick = event => {
    // The type="radio" input is hidden via CSS,
    // but click events bubbled up to the label
    // are proxied to <input /> element
    // causing links to be tracked twice.
    event.stopPropagation();
  };

  setRadioIcon() {
    let icon;
    if (this.props.disabled) {
      icon = this.props.checked ? iconActiveDisabled : iconInactiveDisabled;
    } else {
      icon = this.props.checked ? iconActive : iconInactive;
    }
    return icon;
  }

  render() {
    const { direction } = this.props;

    const radioButton = (
      <label
        className={this.props.className}
        {...hooks.hook(this.props.automationHook)}
      >
        <RadioInput
          disabled={this.props.disabled}
          type="radio"
          onChange={this.handleChange}
          onFocus={this.props.onFocus}
          onBlur={this.props.onBlur}
          checked={this.props.checked}
          name={this.props.name}
          value={this.props.value}
          onClick={this.handleInputClick}
        />
        <RadioIcon
          direction={direction}
          src={this.setRadioIcon()}
          alt={this.props.checkedIconAltAttribute}
        />
        {/* Changed this from Text component in order to accommodate  */}
        <React.Fragment>{this.props.label}</React.Fragment>
      </label>
    );

    return this.props.wrapper
      ? React.cloneElement(this.props.wrapper, { children: radioButton })
      : radioButton;
  }
}

const StyledRadio = styled(Radio)`
  ${props =>
    props.direction === DIRECTION_COLUMN &&
    css`
      display: flex;
      flex-direction: column-reverse;
      align-items: center;
      justify-content: center;
    `};
`;

export default StyledRadio;
